import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ComplianceBar, ComplianceRing } from "@/components/Compliance";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { complianceColor } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  CheckCircle2, Clock, Loader2, AlertTriangle, Eye, CalendarCheck, ChevronRight,
} from "lucide-react";

const KPIS = [
  { key: "completada", label: "Completadas", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  { key: "pendiente", label: "Pendientes", icon: Clock, color: "text-yellow-600", bg: "bg-yellow-50" },
  { key: "en_progreso", label: "En progreso", icon: Loader2, color: "text-blue-600", bg: "bg-blue-50" },
  { key: "vencida", label: "Vencidas", icon: AlertTriangle, color: "text-red-600", bg: "bg-red-50" },
  { key: "en_revision", label: "En revisión", icon: Eye, color: "text-indigo-600", bg: "bg-indigo-50" },
  { key: "today_count", label: "Hoy", icon: CalendarCheck, color: "text-slate-700", bg: "bg-slate-100" },
];

export default function Dashboard() {
  const [period, setPeriod] = useState("mes");
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", period],
    queryFn: () => api.get(`/dashboard?period=${period}`).then((r) => r.data),
  });

  if (isLoading || !data) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }
  const { stats, by_area, by_employee } = data;
  const lowEmployees = by_employee.filter((e) => e.total > 0).slice(0, 5);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Estado de la operación en tiempo real</p>
        </div>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-36 bg-white" data-testid="period-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="hoy">Hoy</SelectItem>
            <SelectItem value="semana">Esta semana</SelectItem>
            <SelectItem value="mes">Este mes</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* KPI grid + compliance */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="grid grid-cols-2 gap-3 lg:col-span-2 lg:grid-cols-3">
          {KPIS.map((k) => (
            <div key={k.key} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`kpi-${k.key}`}>
              <div className={cn("mb-2 inline-flex h-9 w-9 items-center justify-center rounded-lg", k.bg)}>
                <k.icon className={cn("h-5 w-5", k.color)} />
              </div>
              <div className="font-heading text-3xl font-extrabold text-slate-900">{stats[k.key]}</div>
              <div className="text-xs font-medium text-slate-500">{k.label}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center justify-center rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-3 text-sm font-semibold text-slate-500">Cumplimiento general</div>
          <ComplianceRing pct={stats.compliance} />
          <div className="mt-3 text-xs text-slate-400">{stats.total} tareas en el período</div>
        </div>
      </div>

      {stats.needs_review > 0 && (
        <button onClick={() => navigate("/tareas?status=en_revision")} data-testid="review-alert"
          className="flex w-full items-center gap-3 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-left transition hover:bg-indigo-100">
          <Eye className="h-5 w-5 text-indigo-600" />
          <div className="flex-1">
            <div className="font-heading font-bold text-indigo-900">{stats.needs_review} tarea(s) esperan tu revisión</div>
            <div className="text-xs text-indigo-600">Aprueba o rechaza las tareas enviadas por tu equipo</div>
          </div>
          <ChevronRight className="h-5 w-5 text-indigo-400" />
        </button>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Compliance by area (Semáforo) */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="area-compliance">
          <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Cumplimiento por área</h2>
          <div className="space-y-4">
            {by_area.length === 0 && <p className="text-sm text-slate-400">Sin áreas registradas.</p>}
            {by_area.map((a) => (
              <div key={a.id}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">{a.name}</span>
                  <span className={cn("font-bold", complianceColor(a.compliance).text)}>{a.compliance}%</span>
                </div>
                <ComplianceBar pct={a.compliance} showLabel={false} />
              </div>
            ))}
          </div>
        </div>

        {/* Low compliance employees */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="employee-compliance">
          <h2 className="mb-4 font-heading text-lg font-bold text-slate-900">Cumplimiento por funcionario</h2>
          <div className="space-y-3">
            {lowEmployees.length === 0 && <p className="text-sm text-slate-400">Sin datos de funcionarios.</p>}
            {lowEmployees.map((e) => {
              const c = complianceColor(e.compliance);
              return (
                <div key={e.id} className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-bold text-slate-600">
                    {e.avatar ? <img src={e.avatar} alt="" className="h-full w-full object-cover" /> : e.name[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="truncate text-sm font-semibold text-slate-700">{e.name}</span>
                      <span className={cn("text-sm font-bold", c.text)}>{e.compliance}%</span>
                    </div>
                    <ComplianceBar pct={e.compliance} showLabel={false} height="h-2" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
